<template>
  <div class="sidebar">
    <div class="sidebar-logo">
      <img src="../assets/logos/link_logo.svg" />
      <div class="sidebar-version">
        {{ version }}
      </div>
    </div>
    <div class="sidebar-items">
      <template v-for="item in navigation.items">
        <div
          :key="item.title"
          class="sidebar-item-container"
        >
          <router-link
            :id="item.id"
            v-tooltip="{ content: tooltipText(item), placement: 'right' }"
            :to="{ name: item.routerName }"
            class="sidebar-item"
            :class="{ disabled: !canAccess(item.authorizationResource) }"
          >
            <span
              class="sidebar-item__icon"
              v-html="item.icon"
            />
          </router-link>
          <template v-if="item.routerName === 'configuration'">
            <transition name="pop">
              <template v-if="showFeatureTooltip">
                <feature-tooltip
                  data-testid="wellness-tooltip"
                  :title="wellnessTooltip.title"
                  :description="wellnessTooltip.description"
                  :position="wellnessTooltip.position"
                  @close="hideTooltip"
                />
              </template>
            </transition>
          </template>
        </div>
      </template>
    </div>
    <div class="sidebar-footer">
      <div
        id="alerts-toggle-nav"
        v-tooltip="{ content: 'Alerts', placement: 'right' }"
        class="sidebar-item"
        @click.stop="toggleEventSidePanel"
      >
        <span
          class="sidebar-item__icon"
          v-html="navigation.icons.alert"
        />
      </div>
      <div
        id="help-menu-nav"
        v-tooltip="{ content: 'Help', placement: 'right' }"
        class="sidebar-item sidebar-item--help"
        @click.stop="toggleHelpMenu"
      >
        <span
          class="sidebar-item__icon"
          v-html="navigation.icons.help"
        />
        <transition name="pop">
          <template v-if="showHelpMenu">
            <ul
              v-click-outside="toggleHelpMenu"
              class="sidebar-dropdown"
            >
              <li
                id="whats-new-menu-nav"
                class="sidebar-dropdown__item sidebar-dropdown__item--new"
                @click="toggleWhatsNewModal"
              >
                What's New?
              </li>
              <li
                id="getting-started-menu-nav"
                class="sidebar-dropdown__item"
                @click.stop="emitOpenGuide()"
              >
                Starting Guide
              </li>
              <li
                id="installation-menu-nav"
                class="sidebar-dropdown__item"
                @click="externalLink('installation')"
              >
                Installation Guide
              </li>
              <li
                id="notes-menu-nav"
                class="sidebar-dropdown__item"
                @click.stop="externalLink('releaseNotes')"
              >
                Release Notes
              </li>
              <div class="sidebar-dropdown__divider" />
              <li
                id="feedback-menu-nav"
                class="sidebar-dropdown__item"
                @click="toggleFeedbackForm"
              >
                Submit Feedback
              </li>
            </ul>
          </template>
        </transition>
      </div>
      <div
        id="account-menu-nav"
        class="sidebar-item"
        @click.stop="toggleAccountMenu"
      >
        <span class="sidebar__admin-icon">{{ currentUser.initial }}</span>
        <transition name="pop">
          <template v-if="showAccountMenu">
            <ul
              v-click-outside="toggleAccountMenu"
              class="sidebar-dropdown"
            >
              <li class="sidebar-dropdown__item sidebar-dropdown__item-heading">
                {{ currentUser.name }}
                <span class="sidebar-dropdown__item-subtext">
                  {{ currentUser.username }}
                </span>
              </li>
              <div class="sidebar-dropdown__divider" />
              <li
                id="account-details-menu-nav"
                class="sidebar-dropdown__item"
                @click.stop="routeTo('accountDetails')"
              >
                Account
              </li>
              <li
                id="logout-menu-nav"
                class="sidebar-dropdown__item sidebar-dropdown__item--signout"
                @click="logout"
              >
                <span
                  class="dropdown-icon"
                  v-html="navigation.icons.logOut"
                />
                Sign Out
              </li>
            </ul>
          </template>
        </transition>
      </div>
    </div>
    <transition name="fade">
      <template v-if="showWhatsNewModal">
        <whats-new
          data-testid="whats-new-modal"
          @close="toggleWhatsNewModal"
        />
      </template>
    </transition>
    <transition name="fade">
      <template v-if="showFeedbackForm">
        <feedback-form
          data-testid="feedback-modal"
          @close="toggleFeedbackForm"
        />
      </template>
    </transition>
    <transition name="slide-left">
      <template v-if="showEventPanel">
        <event-side-panel @close="toggleEventSidePanel">
        </event-side-panel>
      </template>
    </transition>
  </div>
</template>

<script>
import {ENV} from '../config'

import WhatsNew from './whatsNew.vue'
import FeedbackForm from '@/components/feedbackForm.vue'
import EventSidePanel from '@/components/eventSidePanel.vue'
import FeatureTooltip from '@/components/newFeatureTooltip.vue'
import navMixin from '@/mixins/navMixin.js'
import constants from '@/global/constants.js'

export default {
  name: 'Sidebar',
  components: {
    FeedbackForm,
    EventSidePanel,
    FeatureTooltip,
    WhatsNew,
  },
  mixins: [navMixin],
  data() {
    return {
      showHelpMenu: false,
      showAccountMenu: false,
      showFeedbackForm: false,
      showEventPanel: false,
      showWhatsNewModal: false,
      showFeatureTooltip: true,
      wellnessTooltip: {
        position: 'right',
        title: 'Try BioConnect Mobile',
        description:
          'Use the Wellness Declaration survey to detect potential health risks before they enter your facility!',
      },
    }
  },
  computed: {
    currentUser() {
      const {firstName, lastName, username} = this.getLoggedInUser()
      const _user = {
        name: `${firstName} ${lastName}`,
        initial: this.getInitials(firstName, lastName),
        username,
      }
      return _user
    },
    version() {
      return ENV.version
    },
  },
  watch: {
    showHelpMenu() {
      if (this.showHelpMenu) {
        this.showAccountMenu = false
      }
    },
    showAccountMenu() {
      if (this.showAccountMenu) {
        this.showHelpMenu = false
      }
    },
  },
  created() {
    /*
     * Dont show the wellness tooltip if the users decide to not see it anymore
     * Also dont show if user dont have acess to update settings
     */
    if (localStorage.getItem('wellness') || !this.$can('view', 'settings')) {
      this.showFeatureTooltip = false
    }
  },
  methods: {
    toggleHelpMenu() {
      this.showHelpMenu = !this.showHelpMenu
    },
    toggleAccountMenu() {
      this.showAccountMenu = !this.showAccountMenu
    },
    toggleEventSidePanel() {
      this.showEventPanel = !this.showEventPanel
    },
    toggleFeedbackForm() {
      this.showFeedbackForm = !this.showFeedbackForm
    },
    toggleWhatsNewModal() {
      this.showWhatsNewModal = !this.showWhatsNewModal
    },
    hideTooltip() {
      localStorage.setItem('wellness', true)
      this.showFeatureTooltip = false
    },
    externalLink(location) {
      const _releaseNotesRoute = this.$router.resolve({name: 'releaseNotes'})
      const _linkLocations = {
        installation: 'https://bioconnect.com/link/install/',
        releaseNotes: _releaseNotesRoute.href,
      }
      window.open(_linkLocations[location], '_blank')
      this.toggleHelpMenu()
    },
    routeTo(routeName) {
      this.$router.push({name: routeName})
      this.toggleAccountMenu()
    },
    emitOpenGuide() {
      this.$root.$emit('open-guide-panel')
      this.toggleHelpMenu()
    },
    canAccess(authorizationResource) {
      // Not every object has a restriction, so if it is null we just assume they have access
      if (authorizationResource) {
        return this.$can('view', authorizationResource)
      }
      return true
    },
    tooltipText(item) {
      if (this.canAccess(item.authorizationResource)) {
        return item.title
      }
      return constants.actionNotAvailableMessage
    },
  },
}
</script>

<style scoped lang="scss">
.sidebar {
  @include flex-display();
  @include flex-direction(column);
  @include flex-align-items(center);
  @include flex-justify-content(space-between);
  position: fixed;
  height: auto;
  min-height: 100vh;
  width: $sidebar-width;
  background: linear-gradient($black, $dark-grey);
  color: $white;
  z-index: 100;
  &-logo {
    @include flex-display();
    @include flex-justify-content(center);
    @include flex-align-items(center);
    @include flex-direction(column);
    height: 90px;
    img {
      width: 75%;
      height: 60%;
    }
  }
  &-items {
    flex-grow: 2;
  }
  &__admin-icon {
    @include flex-display();
    @include flex-justify-content(center);
    @include flex-align-items(center);
    border: 2px solid $white;
    border-radius: 50%;
    padding: 0.6em;
    width: 1.5em;
    height: 1.5em;
    font-weight: 600;
    transition: border-color 0.2s ease-out;
    text-transform: uppercase;
    &:hover {
      cursor: pointer;
      border-color: $green;
    }
  }
  &-item {
    @include flex-display();
    @include flex-justify-content(center);
    @include flex-align-items(center);
    color: $white;
    margin: 1em 0;
    font-size: $small-font;
    border-radius: 50%;
    transition: background 0.3s;
    border: 2px solid transparent;
    position: relative;
    @media #{$large-display} {
      font-size: $default-font;
    }
    &-container {
      position: relative;
    }
    &__icon {
      @include flex-display();
      @include flex-justify-content(center);
      @include flex-align-items(center);
      padding: 0.8em;
      width: 1.5em;
      height: 1.5em;
    }
    &.router-link-active {
      box-sizing: border-box;
      border-color: $green;
      cursor: default;
    }
    &:hover {
      cursor: pointer;
      border-radius: 50%;
      background: $dark-grey;
    }
    &.disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    &--help {
      &:before {
        content: "";
        position: absolute;
        top: -0.1px;
        right: -0.1px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background-color: $green;
      }
    }
  }
  &-version {
    color: $grey-text;
    font-weight: 600;
    font-size: $small-font;
  }
  &-dropdown {
    position: fixed;
    padding: 0;
    bottom: 0;
    left: calc(#{$sidebar-width} + 1em);
    z-index: 100;
    background: #ffffff;
    color: #333333;
    border-radius: 5px;
    box-shadow: 0px 1px 10px 0px rgba(0, 0, 0, 0.2);
    list-style: none;
    min-width: 200px;
    &__divider {
      border-bottom: 1px solid $light-grey;
    }
    &__item {
      @include flex-display();
      @include flex-align-items(center);
      padding: 1em 1.5em;
      color: $dark-grey;
      font-size: $large-font;
      @media #{$large-display} {
        font-size: $default-font;
      }
      .dropdown-icon {
        @include flex-display();
        @include flex-align-items(center);
        margin-right: 0.5em;
        width: 1em;
      }
      &--signout {
        color: $red;
      }
      &--new {
        color: $green;
      }
      &-heading {
        font-weight: 600;
        @include flex-display();
        @include flex-direction(column);
        @include flex-align-items(flex-start);
        &:hover {
          cursor: default !important;
          background-color: $white !important;
        }
      }
      &-subtext {
        font-size: $tiny-font;
        color: $grey-text;
      }
      &:hover {
        background-color: $off-white;
        cursor: pointer;
        &:last-child {
          border-bottom-left-radius: 5px;
          border-bottom-right-radius: 5px;
        }
        &:first-child {
          border-top-left-radius: 5px;
          border-top-right-radius: 5px;
        }
      }
      &:last-child {
        border-bottom: none;
      }
    }
  }
  &-footer {
    flex-shrink: 1;
  }
}
@media #{$mobile} {
  .sidebar {
    display: none;
  }
}
</style>
